import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { api, type UnitDetail } from '../lib/api'
import { AccessNotice, SeatNotice } from '../components/SeatNotice'
import { ReadingView, type ReadingRecord } from '../components/viewer/ReadingView'
import { RecordMissing } from '../components/viewer/RecordMissing'
import { useSession } from '../lib/session'

/**
 * One searchable unit of any type, readable, with a citation.
 *
 * A photograph, an advertisement and a notice are cited exactly as an article
 * is, so this is the same reading view rather than four of them. What differs
 * between the types is what the record holds, and the view shows what it has.
 */

export function UnitPage() {
  const { unitId = '' } = useParams()
  const { config, can } = useSession()

  const unit = useQuery({
    queryKey: ['unit', unitId],
    queryFn: () => api.get<UnitDetail>(`/units/${unitId}?words=true`),
    retry: false,
  })

  if (unit.isError) {
    return (
      <div className="p-4 md:p-6">
        <SeatNotice error={unit.error} onRetry={() => void unit.refetch()} />
        <AccessNotice error={unit.error} />
        <RecordMissing error={unit.error} what="record" onRetry={() => void unit.refetch()} />
      </div>
    )
  }
  if (!unit.data) {
    return <p className="p-6 text-[15px] text-ink2">Opening the record.</p>
  }

  const data = unit.data
  const fields = data.fields ?? {}
  const record: ReadingRecord = {
    kind: data.unit_type,
    title: data.title,
    subtitle: data.subtitle,
    byline: data.byline,
    pubDate: data.pub_date,
    pageLabel: data.section_label ?? (data.page_number ? String(data.page_number) : null),
    pageId: data.page_id,
    region: data.region,
    route: data.route,
    engine: data.engine,
    confidence: data.confidence,
    wordCount: data.word_count,
    isWire: data.is_wire,
    wireService: data.wire_service,
    columnsLabel: data.columns_label,
    publication: data.citation?.publication ?? null,
    correctionSource: data.correction_source,
    textRaw: data.text_raw,
    textCorrected: data.text_corrected,
    blocks: data.blocks ?? [],
    citation: data.citation,
    permalink: data.permalink,
    needsReview: fields.needs_review === true,
    jumpTo: data.jump?.to ?? null,
    editionId: data.edition_id ?? null,
    pageNumber: data.page_number,
  }

  // Articles, notices and listings are article rows and can be corrected.
  const editable =
    can('review.item.edit') && ['article', 'notice', 'listing'].includes(data.unit_type)

  return (
    <ReadingView
      record={record}
      productName={config?.product_name ?? ''}
      backHref="/search"
      actions={
        editable ? (
          <Link to={`/article/${data.object_id}?edit=1`} className="btn-secondary no-underline">
            Edit article
          </Link>
        ) : null
      }
    />
  )
}
